import React, {useMemo} from 'react';
import {Editor, Transforms, Text, Element as SlateElement} from 'slate';
import {useSlate, useSlateSelector} from 'slate-react';

import {CommentThreadCard} from '../../../components/CommentPanel/CommentPanel';
import {useCommentStore} from '../../../store/reportStore/commentStore';
import type {Comment} from '../constants/ReportBuilderInterfce';
import {useEditorUI} from '../editor-core/contexts/EditorUIContext';
import {removeEmptyDraft} from '../utils/commentUtils';

function readElementComments(el: SlateElement): Comment[] | undefined {
  const v = (el as {comments?: unknown}).comments;

  return Array.isArray(v) ? (v as Comment[]) : undefined;
}

export const CommentGroupOverlay = (): React.JSX.Element | null => {
  const editor = useSlate();
  const activeCommentNodeId = useCommentStore((s) => s.activeCommentNodeId);
  const setActiveCommentNodeId = useCommentStore(
    (s) => s.setActiveCommentNodeId
  );
  const documentRevision = useSlateSelector((ed) => ed.children);
  const {commentPanelOpen, onCommentAdded} = useEditorUI();

  const activeNodeEntry = useMemo(() => {
    if (!activeCommentNodeId) return null;
    const matches = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: (n) => {
          if (Editor.isEditor(n) || !SlateElement.isElement(n)) return false;
          const list = readElementComments(n);

          return (
            list !== undefined &&
            list.some(
              (c) =>
                c.threadId === activeCommentNodeId ||
                c.id === activeCommentNodeId
            )
          );
        }
      })
    );

    return matches.length > 0 ? matches[0] : null;
  }, [editor, activeCommentNodeId, documentRevision]);

  if (
    !activeCommentNodeId ||
    !activeNodeEntry ||
    commentPanelOpen
  ) {
    return null;
  }

  const [node, path] = activeNodeEntry;
  if (!SlateElement.isElement(node)) return null;
  const element = node;
  const comments = (readElementComments(element) ?? []).filter(
    (c) => c.threadId === activeCommentNodeId || c.id === activeCommentNodeId
  );
  if (comments.length === 0) return null;

  return (
    <div className="comment-thread-sidebar">
      <div
        className="comment-group-overlay"
        contentEditable={false}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        onMouseUp={(e) => {
          e.stopPropagation();
        }}
      >
        <CommentThreadCard
          key={activeCommentNodeId}
          thread={comments}
          editor={editor}
          element={element}
          path={path}
          isActive
          style={{position: 'relative', width: '100%'}}
          onClick={() => {}}
          onClose={() => {
            if (activeCommentNodeId) {
              removeEmptyDraft(editor, activeCommentNodeId);
            }
            setActiveCommentNodeId(null);
          }}
          onDelete={(id) => {
            const filtered = (readElementComments(element) ?? []).filter(
              (c) => c.id !== id
            );
            if (
              filtered.length === 0 ||
              !filtered.some((c) => c.threadId === activeCommentNodeId)
            ) {
              Editor.withoutNormalizing(editor, () => {
                Transforms.unsetNodes(editor, 'commentThreadId', {
                  at: [],
                  match: (n) =>
                    Text.isText(n) &&
                    n.commentThreadId === activeCommentNodeId,
                  mode: 'all'
                });
                Transforms.unsetNodes(editor, 'commentThreadResolved', {
                  at: [],
                  match: (n) =>
                    Text.isText(n) &&
                    n.commentThreadId === activeCommentNodeId,
                  mode: 'all'
                });
              });
            }
            if (filtered.length === 0) {
              Transforms.setNodes(
                editor,
                {comments: undefined, commentId: undefined} as any,
                {at: path}
              );
            } else {
              Transforms.setNodes(editor, {comments: filtered} as any, {
                at: path
              });
            }
            if (
              !filtered.some((c) => c.threadId === activeCommentNodeId)
            ) {
              setActiveCommentNodeId(null);
            }

            onCommentAdded();
          }}
        />
      </div>
    </div>
  );
};
